import { ReactNode, useState } from 'react'
import {
  Field,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react'
import { CheckMarkIcon } from '@/common/icons/arrows.tsx'
import { cn } from '@/common/utilities.ts'
import { useTranslation } from 'react-i18next'

export type ListItem = { id: number; name: string }

type SimpleSelectorProps = {
  list: readonly ListItem[]
  onChange: (value: ListItem) => void
  className?: string
  icon: ReactNode
  disabled?: boolean
  readOnly?: boolean
  selected?: { id: number; name: string }
  setSelected?: React.Dispatch<
    React.SetStateAction<{ id: number; name: string }>
  >
}

export default function SimpleSelector({
  list,
  onChange,
  className,
  icon,
  disabled = false,
  readOnly = false,
}: SimpleSelectorProps) {
  const { t: translate } = useTranslation()
  const [selected, setSelected] = useState<ListItem>(list[0])

  const handleOnChange = (value: ListItem) => {
    setSelected(value)
    onChange(value)
  }

  return (
    <Field disabled={disabled || readOnly}>
      <Listbox value={selected} onChange={handleOnChange}>
        {/*<Label className="block text-sm font-medium leading-6 text-gray-900">*/}
        {/*  Assigned to*/}
        {/*</Label>*/}
        <div className={cn('relative', className)}>
          <ListboxButton
            className={cn(
              'relative w-full rounded-md bg-white py-1.5 pr-10 text-left text-gray-500 font-medium focus:outline-none focus:ring-2 focus:ring-primary-600 sm:text-sm sm:leading-6',
              !readOnly &&
                'pl-3 cursor-pointer shadow-sm ring-1 ring-inset ring-gray-300 text-gray-900'
            )}
          >
            <span className="block truncate">{translate(selected.name)}</span>
            <span
              className={cn(
                'hidden pointer-events-none absolute inset-y-0 right-0 items-center pr-2',
                !readOnly && 'flex'
              )}
            >
              {icon}
            </span>
          </ListboxButton>

          <ListboxOptions
            transition
            className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
          >
            {list.map((listItem) => (
              <ListboxOption
                key={listItem.id}
                value={listItem}
                className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-primary-600 data-[focus]:text-white"
              >
                <span className="block font-normal group-data-[selected]:font-semibold">
                  {translate(listItem.name)}
                </span>

                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-600 group-data-[focus]:text-white [.group:not([data-selected])_&]:hidden">
                  <CheckMarkIcon />
                </span>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>
    </Field>
  )
}
